import React, { useState } from 'react';
import { isAxiosError } from 'axios';
import styled from 'styled-components';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import Checkbox from 'components/shared/form/Checkbox';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import { useDebounce } from 'hooks/useDebounce';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { PERMISSIONS } from 'models/Profile';
import {
  createSftpUser,
  indexSftpUsers,
  previewSftpKey,
  SftpDeliverable,
  SftpUser,
  SftpUserParams,
  sftpUserQueryKeys,
  SftpUsersIndex,
  updateSftpUser,
} from 'services/api/admin/sftpUsers';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

const DELIVERABLES: { id: SftpDeliverable; label: string }[] = [
  { id: 'providerDocuments', label: 'Provider documents' },
  { id: 'patientExport', label: 'Patient export' },
];

const USERNAME_PATTERN = /^[A-Za-z0-9_][A-Za-z0-9_-]{2,99}$/;

const ACCESS_LABEL = { inbound: 'Inbound', outbound: 'Outbound', both: 'Inbound + Outbound' } as const;

export const suggestUsername = (clientName: string) =>
  clientName
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100)
    .replace(/-+$/, '');

const syncErrorText = (user: SftpUser) =>
  user.lastSyncError ? `Not saved to AWS: ${user.lastSyncError.replace(/\.+$/, '')}.` : 'Not saved to AWS.';

const saveErrorToast = (error: unknown) =>
  validationErrorMessage(error) ??
  (isAxiosError(error) && error.response?.status === 502 ? error.response.data?.message : undefined);

const SCRIPTS_CHANGE = 'The customer will need to update where their scripts upload and download.';

const accessChangeWarning = (user: SftpUser, inbound: boolean, outbound: boolean) => {
  if (inbound && outbound)
    return `This login will use separate /inbound and /outbound folders instead of one home folder. ${SCRIPTS_CHANGE}`;
  if (user.access === 'both')
    return `This login will use one home folder instead of separate /inbound and /outbound folders. ${SCRIPTS_CHANGE}`;
  if (outbound) return 'The folder stays the same, but the customer will no longer be able to upload files to Olio.';
  return 'The folder stays the same, but Olio exports will stop arriving in it.';
};

type Draft = { inbound: boolean; outbound: boolean; deliverables: Record<SftpDeliverable, boolean> };

type Props = {
  client: { id: string; name: string };
  onCancel: () => void;
};

export default function SftpUsersModal({ client, onCancel }: Props) {
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminClientEdit);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const startEditing = (id: string | 'new' | null, carried: Draft | null = null) => {
    setDraft(carried);
    setEditingId(id);
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: sftpUserQueryKeys.index(client.id),
    queryFn: ({ signal }) => indexSftpUsers(client.id, signal),
  });
  const sftpUsers = data?.data ?? [];
  const editing = editingId === 'new' ? 'new' : sftpUsers.find((user) => user.id === editingId);

  if (editing) {
    const outboundOwner = sftpUsers.find(
      (user) => user.active && user.outbound && (editing === 'new' || user.id !== editing.id)
    );
    return (
      <SftpUserForm
        key={editing === 'new' ? 'new' : editing.id}
        client={client}
        sftpUser={editing === 'new' ? null : editing}
        outboundOwner={outboundOwner?.username ?? null}
        exportDeliverables={data?.exportConfiguration?.enabledDeliverableTypes ?? {}}
        draft={draft}
        onDone={() => startEditing(null)}
        onEdit={startEditing}
        onCancel={onCancel}
      />
    );
  }

  return (
    <StandardModal title='SFTP Users' subtitle={client.name} onCancel={onCancel}>
      <StandardModal.ModalContent>
        <div style={{ textAlign: 'left' }}>
          {isLoading && <HelperText>Loading…</HelperText>}
          {isError && <ErrorText>SFTP logins failed to load.</ErrorText>}
          {data && sftpUsers.length === 0 && <HelperText>This client has no SFTP logins.</HelperText>}
          {sftpUsers.map((user) => (
            <UserRow key={user.id} data-testid='sftp-user-row'>
              <div style={{ minWidth: 0 }}>
                <Username>{user.username}</Username>
                <Meta>
                  {ACCESS_LABEL[user.access]} · <Fingerprint>{user.keyFingerprint}</Fingerprint>
                </Meta>
                <Meta>
                  <SyncStatus user={user} />
                  {user.lastChangedBy && <> · last changed by {user.lastChangedBy.name}</>}
                </Meta>
                {user.alsoUsedBy.length > 0 && <Meta>Same key as {user.alsoUsedBy.join(', ')}</Meta>}
              </div>
              {canEdit && (
                <Button variant='ghost' onClick={() => startEditing(user.id)}>
                  Edit
                </Button>
              )}
            </UserRow>
          ))}
        </div>
      </StandardModal.ModalContent>
      <StandardModal.Actions>
        <ButtonGroup>
          <Button variant='ghost' onClick={onCancel}>
            Close
          </Button>
          {canEdit && (
            <Button onClick={() => startEditing('new')} disabled={!data}>
              Add SFTP User
            </Button>
          )}
        </ButtonGroup>
      </StandardModal.Actions>
    </StandardModal>
  );
}

function SyncStatus({ user }: { user: SftpUser }) {
  if (user.syncState === 'failed') return <ErrorInline>{syncErrorText(user)}</ErrorInline>;
  if (!user.active) return <>Deactivated</>;
  if (user.syncState === 'pending') return <>Saving…</>;
  return <>Active</>;
}

type FormProps = {
  client: { id: string; name: string };
  sftpUser: SftpUser | null;
  outboundOwner: string | null;
  exportDeliverables: Partial<Record<SftpDeliverable, boolean>>;
  draft: Draft | null;
  onDone: () => void;
  onEdit: (id: string, carried: Draft) => void;
  onCancel: () => void;
};

function SftpUserForm({
  client,
  sftpUser,
  outboundOwner,
  exportDeliverables,
  draft,
  onDone,
  onEdit,
  onCancel,
}: FormProps) {
  const isNew = !sftpUser;
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const [username, setUsername] = useState(sftpUser?.username ?? suggestUsername(client.name));
  const [inbound, setInbound] = useState(draft?.inbound ?? sftpUser?.inbound ?? true);
  const [outbound, setOutbound] = useState(draft?.outbound ?? sftpUser?.outbound ?? false);
  const [deliverables, setDeliverables] = useState<Record<SftpDeliverable, boolean>>(
    draft?.deliverables ?? {
      providerDocuments: exportDeliverables.providerDocuments ?? false,
      patientExport: exportDeliverables.patientExport ?? false,
    }
  );
  const [sshPublicKey, setSshPublicKey] = useState('');
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  const trimmedKey = sshPublicKey.trim();
  const debouncedKey = useDebounce(trimmedKey, 300);
  const keySettled = debouncedKey === trimmedKey;
  const keyTooShort = keySettled && trimmedKey.length > 0 && trimmedKey.length <= 40;
  const preview = useQuery({
    queryKey: ['adminSftpKeyPreview', client.id, debouncedKey],
    queryFn: () => previewSftpKey(client.id, debouncedKey),
    enabled: debouncedKey.length > 40,
    retry: false,
  });
  const alsoUsedBy = (preview.data?.alsoUsedBy ?? [])
    .filter((login) => login.username !== sftpUser?.username)
    .map((login) => (login.clientName ? `${login.username} (${login.clientName})` : login.username));

  const outboundBlocked = !!outboundOwner && !sftpUser?.outbound;
  const addingOutbound = outbound && !sftpUser?.outbound;
  const accessChanged = !!sftpUser && (inbound !== sftpUser.inbound || outbound !== sftpUser.outbound);
  const usernameError =
    isNew && !USERNAME_PATTERN.test(username)
      ? 'Use 3–100 letters, digits, underscores or dashes, not starting with a dash.'
      : null;
  const keyRequired = isNew || trimmedKey.length > 0;
  const keyReady = !keyRequired || (keySettled && !!preview.data && !preview.isFetching);
  const canSave = (inbound || outbound) && !usernameError && keyReady && !outboundBlocked;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: sftpUserQueryKeys.index(client.id) });

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: (params: SftpUserParams) =>
      sftpUser ? updateSftpUser(client.id, sftpUser.id, params) : createSftpUser(client.id, params),
    onSuccess: (saved: SftpUser) => {
      invalidate();
      addToast({ text: `${saved.username} saved.` });
      onDone();
    },
    onError: async (error) => {
      await invalidate();
      if (!isNew || (isAxiosError(error) && error.response?.status === 422)) return;
      const created = queryClient
        .getQueryData<SftpUsersIndex>(sftpUserQueryKeys.index(client.id))
        ?.data.find((user) => user.username === username);
      if (created) onEdit(created.id, { inbound, outbound, deliverables });
    },
    errorToast: saveErrorToast,
  });

  const save = () =>
    mutate({
      ...(isNew ? { username } : {}),
      inbound,
      outbound,
      ...(outbound ? { deliverables } : {}),
      ...(trimmedKey ? { sshPublicKey: trimmedKey } : {}),
    });

  if (sftpUser && confirmingDeactivate) {
    return (
      <StandardModal
        title={`Deactivate ${sftpUser.username}?`}
        subtitle={client.name}
        colorTheme={ColorThemeName.Danger}
        onCancel={onCancel}>
        <StandardModal.ModalContent>
          <div style={{ textAlign: 'left' }}>The customer will no longer be able to connect with this login.</div>
        </StandardModal.ModalContent>
        <StandardModal.Actions>
          <ButtonGroup>
            <Button variant='ghost' disabled={isPending} onClick={() => setConfirmingDeactivate(false)}>
              Cancel
            </Button>
            <Button variant='danger' loading={isPending} onClick={() => mutate({ active: false })}>
              Deactivate
            </Button>
          </ButtonGroup>
        </StandardModal.Actions>
      </StandardModal>
    );
  }

  return (
    <StandardModal
      title={isNew ? 'Add SFTP User' : `Edit ${sftpUser.username}`}
      subtitle={client.name}
      onCancel={onCancel}>
      <StandardModal.ModalContent>
        <div style={{ textAlign: 'left' }}>
          <InputGroup>
            <FieldLabel>Username</FieldLabel>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={!isNew}
              $hasError={!!usernameError}
            />
            {usernameError && <ErrorText>{usernameError}</ErrorText>}
            {isNew && <HelperText>Can&apos;t be changed after saving.</HelperText>}
          </InputGroup>

          <InputGroup>
            <FieldLabel>Access</FieldLabel>
            <CheckboxList>
              <Checkbox label='Inbound — the customer uploads files to Olio' checked={inbound} onChange={setInbound} />
              <Checkbox
                label='Outbound — the customer downloads files Olio exports'
                checked={outbound}
                onChange={setOutbound}
                disabled={outboundBlocked}
              />
            </CheckboxList>
            {outboundBlocked && (
              <HelperText>
                {outboundOwner} already downloads this client&apos;s exports. A client has one outbound login.
              </HelperText>
            )}
            {!inbound && !outbound && <ErrorText>Pick inbound, outbound or both.</ErrorText>}
            {accessChanged && (inbound || outbound) && (
              <ErrorText data-testid='sftp-layout-warning'>
                {accessChangeWarning(sftpUser, inbound, outbound)}
              </ErrorText>
            )}
          </InputGroup>

          {outbound && (
            <InputGroup>
              <FieldLabel>Exports to deliver</FieldLabel>
              <CheckboxList>
                {DELIVERABLES.map(({ id, label }) => (
                  <Checkbox
                    key={id}
                    label={label}
                    checked={deliverables[id]}
                    onChange={(checked) => setDeliverables((prev) => ({ ...prev, [id]: checked }))}
                  />
                ))}
              </CheckboxList>
              {!Object.values(deliverables).some(Boolean) && (
                <HelperText>Nothing is exported to this login until at least one is ticked.</HelperText>
              )}
              {addingOutbound && (
                <HelperText>This login will be able to download PHI that Olio exports for {client.name}.</HelperText>
              )}
            </InputGroup>
          )}

          <InputGroup>
            <FieldLabel>{isNew ? 'Public key' : 'Replace public key'}</FieldLabel>
            <KeyInput
              value={sshPublicKey}
              onChange={(e) => setSshPublicKey(e.target.value)}
              placeholder={isNew ? 'ssh-rsa AAAA… customer@example.com' : 'Leave empty to keep the current key'}
              rows={4}
              aria-label='Public key'
            />
            {keySettled && preview.data && (
              <KeyPreview data-testid='sftp-key-preview'>
                <div>
                  {preview.data.type}, {preview.data.bits} bits
                </div>
                <Fingerprint>{preview.data.fingerprint}</Fingerprint>
                <div>Confirm this fingerprint with the sender before saving.</div>
                {alsoUsedBy.length > 0 && <div>The same key is already used by {alsoUsedBy.join(', ')}.</div>}
              </KeyPreview>
            )}
            {keyTooShort && <HelperText>Paste the whole public key.</HelperText>}
            {keySettled && preview.error && (
              <ErrorText>{validationErrorMessage(preview.error) ?? "Couldn't read that key."}</ErrorText>
            )}
          </InputGroup>

          {sftpUser?.syncState === 'failed' && <ErrorText>{syncErrorText(sftpUser)} Save again to retry.</ErrorText>}
        </div>
      </StandardModal.ModalContent>
      <StandardModal.Actions>
        <ButtonGroup>
          <Button variant='ghost' onClick={onDone}>
            Back
          </Button>
          {sftpUser && (
            <Button
              variant='ghost'
              disabled={isPending}
              onClick={() => (sftpUser.active ? setConfirmingDeactivate(true) : mutate({ active: true }))}>
              {sftpUser.active ? 'Deactivate' : 'Reactivate'}
            </Button>
          )}
          <Button onClick={() => save()} disabled={isPending || !canSave}>
            Save
          </Button>
        </ButtonGroup>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const UserRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.black10};
`;

const Username = styled.p`
  margin: 0;
  font-weight: 500;
`;

const Meta = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  color: ${colors.black50};
  overflow-wrap: anywhere;
`;

const Fingerprint = styled.code`
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const KeyInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  font-family: monospace;
  font-size: 12px;
  padding: 8px;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  resize: vertical;
`;

const KeyPreview = styled.div`
  margin-top: 8px;
  padding: 8px;
  font-size: 12px;
  background: ${colors.black5};
  border-radius: 4px;
  display: grid;
  gap: 4px;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
`;

const HelperText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${colors.black50};
`;

const ErrorText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${colors.red100};
`;

const ErrorInline = styled.span`
  color: ${colors.red100};
`;
